import { useEffect, useState, type ReactNode } from "react";
import { ArrowUpRight, Globe2, LockKeyhole } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { useTranslation, setStoredLanguage } from "@/i18n";
import { VNextButton } from "./foundations";
import "./welcome.css";

/** Decorative only. The lifecycle never owns authentication or wallet state. */
export function AwakeningVisual({ compact = false }: { compact?: boolean }) {
  const [paused, setPaused] = useState(() => typeof document !== "undefined" && document.hidden);
  useEffect(() => {
    const update = () => setPaused(document.hidden);
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);
  return <div className={`dt-awakening${compact ? " dt-awakening--compact" : ""}`} data-paused={paused} aria-hidden="true">
    <div className="dt-awakening-world" />
    <div className="dt-awakening-orbits"><i /><i /><i /></div>
    <div className="dt-awakening-mark"><Logo withText={false} size="lg" /></div>
  </div>;
}

export function WelcomeLanguage() {
  const { i18n, t } = useTranslation();
  const zh = i18n.language.startsWith("zh");
  return <button type="button" className="dt-welcome-language" aria-label={t("welcome.language")} onClick={() => {
    const next = zh ? "en" : "zh";
    setStoredLanguage(next);
    void i18n.changeLanguage(next);
  }}><Globe2 aria-hidden="true" />{zh ? "English" : "中文"}</button>;
}

export interface WelcomeViewProps {
  state?: "ready" | "restoring" | "authenticating";
  onCreate?: () => void;
  onExisting?: () => void;
  note?: ReactNode;
}

export function WelcomeView({ state = "ready", onCreate, onExisting, note }: WelcomeViewProps) {
  const { t } = useTranslation();
  return <section className="dt-welcome" aria-busy={state !== "ready"}>
    <header className="dt-welcome-top"><span className="dt-welcome-wordmark">DeTrust</span><WelcomeLanguage /></header>
    <AwakeningVisual />
    <div className="dt-welcome-copy"><h1>{t("welcome.title")}</h1><p>{t("welcome.description")}</p></div>
    <div className="dt-welcome-actions">
      {state === "ready" ? <><VNextButton className="dt-welcome-primary" onClick={onCreate}>{t("welcome.create")}<ArrowUpRight aria-hidden="true" /></VNextButton><VNextButton className="dt-welcome-existing" variant="quiet" onClick={onExisting}>{t("welcome.existing")}</VNextButton></> : <p className="dt-welcome-status" role="status">{t(state === "restoring" ? "welcome.restoring" : "welcome.authenticating")}</p>}
      {note && <div className="dt-welcome-note">{note}</div>}
      <p className="dt-welcome-security"><LockKeyhole aria-hidden="true" />{t("welcome.security")}</p>
    </div>
  </section>;
}

/** Decorative only: children mount immediately; this timer removes artwork, not a gate. */
export function WelcomeArrival({ children }: { children: ReactNode }) {
  const [arriving, setArriving] = useState(() => typeof window !== "undefined" && !(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false));
  useEffect(() => {
    if (!arriving) return;
    const preference = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    const consume = (event: MediaQueryListEvent) => { if (event.matches) setArriving(false); };
    preference?.addEventListener("change", consume);
    const timer = window.setTimeout(() => setArriving(false), 1200);
    return () => { window.clearTimeout(timer); preference?.removeEventListener("change", consume); };
  }, [arriving]);
  return <div className="dt-welcome-arrival">{children}{arriving && <div className="dt-welcome-arrival-art" aria-hidden="true"><AwakeningVisual compact /></div>}</div>;
}
